import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { MDBCollapse, MDBRow, MDBCol, MDBIcon } from 'mdb-react-ui-kit'
import CustomAlert from '../../shared/CustomAlert'
import ContractingPacket from './../ContractingPacket/ContractingPacket.component'
import UserProfileService from './../../../shared/services/UserProfile.service'
import ACMStore from './../AgentContractManager.store'
import ContentHtml from './../../content/ContentHtml/ContentHtml.component'
import UserContractCartItemFactory from './../../../shared/factories/user-contract-cart-item.factory'

import './OnboardingCheckout.scss'

const OnboardingCheckout = ({ onValidityChange }) => {
  const [allCartItems, setAllCartItems] = useState([])
  const [isLoadingItems, setIsLoadingItems] = useState(true)

  // Fetch ALL cart items directly from database to avoid filtered items
  // This ensures we validate ALL items, not just the ones that pass visibility filters
  useEffect(() => {
    const fetchAllCartItems = async () => {
      if (!ACMStore?.Cart?.id()) {
        setIsLoadingItems(false)
        setAllCartItems([])
        return
      }

      try {
        const cartId = ACMStore.Cart.id()
        const items = await UserContractCartItemFactory.findAllByCartId(cartId)
        setAllCartItems(items || [])
        console.log('[OnboardingCheckout] Fetched', items?.length || 0, 'cart items from database')
      } catch (error) {
        console.error('[OnboardingCheckout] Error fetching cart items:', error)
        setAllCartItems([])
      } finally {
        setIsLoadingItems(false)
      }
    }

    fetchAllCartItems()
  }, [ACMStore?.Cart?.id(), ACMStore.cartItemsVersion])

  // Check if all cart items are completed, accounting for additional links items
  // Items with additional_link or is_both_links MUST be COMPLETED (cannot be ABANDONED or EXECUTED)
  const completedContracts = React.useMemo(() => {
    if (isLoadingItems) return false

    const cartItems = allCartItems.length > 0 ? allCartItems : ACMStore?.CartItems || []

    if (!cartItems || cartItems.length === 0) {
      console.log('[OnboardingCheckout] No cart items found')
      return false
    }

    console.log(
      '[OnboardingCheckout] Checking',
      cartItems.length,
      'cart items (from',
      allCartItems.length > 0 ? 'database' : 'store',
      ')'
    )

    const results = cartItems.map((CartItem, index) => {
      const itemStatus = CartItem.get('item_status')
      const isBothLinks = parseInt(CartItem.get('is_both_links')) || 0
      const isAdditionalLinks = CartItem.get('additional_link')
      const carrierId = CartItem.get('carrier_id')
      const itemGroup = CartItem.get('item_group')
      const itemId = CartItem.get('id')

      const hasAdditionalLinks =
        isBothLinks === 1 ||
        (isAdditionalLinks != null && isAdditionalLinks !== '' && String(isAdditionalLinks).trim().length > 0)

      let isValid = false
      if (hasAdditionalLinks) {
        isValid = itemStatus === 'COMPLETED'
        if (!isValid) {
          console.log('[OnboardingCheckout] Item', index, 'INVALID - has additional links but status is', itemStatus, {
            itemId,
            carrierId,
            itemGroup,
            isBothLinks,
            hasAdditionalLink: !!isAdditionalLinks,
            itemStatus,
          })
        }
      } else {
        isValid = itemStatus === 'COMPLETED' || itemStatus === 'ABANDONED'
      }

      return { isValid, itemId, carrierId, itemStatus, hasAdditionalLinks }
    })

    const allValid = results.every(r => r.isValid)
    const invalidItems = results.filter(r => !r.isValid)

    console.log('[OnboardingCheckout] Validation result:', {
      totalItems: cartItems.length,
      allValid,
      invalidCount: invalidItems.length,
      invalidItems: invalidItems.map(i => ({
        itemId: i.itemId,
        carrierId: i.carrierId,
        status: i.itemStatus,
        hasAdditionalLinks: i.hasAdditionalLinks,
      })),
    })

    return allValid
  }, [allCartItems, isLoadingItems, ACMStore?.CartItems])

  useEffect(() => {
    console.log('[OnboardingCheckout] completedContracts changed to:', completedContracts)
    if (typeof onValidityChange === 'function') onValidityChange(completedContracts)
  }, [completedContracts, onValidityChange])

  return (
    <>
      <div id='OnboardingCheckoutComponent'>
        <MDBRow>
          <MDBCol size='12'>
            <MDBCollapse open={!completedContracts}>
              <ContentHtml contentId='103' />
            </MDBCollapse>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol size='12'>
            <MDBCollapse open={!completedContracts}>
              {ACMStore.packets.map((packetId, idx) => (
                <ContractingPacket
                  key={`contracting-packet-${idx}`}
                  packetId={packetId}
                  Packet={ACMStore?.Packet}
                  allCartItems={allCartItems}
                />
              ))}
            </MDBCollapse>
            <MDBCollapse open={completedContracts}>
              <CustomAlert color='success'>
                <div className='content-wrapper'>
                  <table>
                    <thead>
                      <tr>
                        <th colSpan='2'>
                          Contracts Requested!
                          <hr />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <MDBIcon icon='thumbs-up' />
                        </td>
                        <td>
                          <h5>Nice Work, {UserProfileService.get('u_fname')}!</h5>
                          <p>
                            The good news is you're past the hard part. Click the green{' '}
                            <span>
                              Start Next Stage&nbsp;
                              <MDBIcon icon='arrow-right' />
                            </span>{' '}
                            button above to move on to the next stage of your onboarding journey.
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CustomAlert>
            </MDBCollapse>
          </MDBCol>
        </MDBRow>
      </div>
    </>
  )
}

export default observer(OnboardingCheckout)
